import { uniq, uniqBy, mapValues, keyBy } from 'lodash';
import { ReactionCodeValue } from '../../../reactions/reaction-types';
import { assertDefined, assertFields, isDefined } from '../../../ts-utils';
import { fromServerVotes } from '../vote-service/vote-service-mappers';
import { assertServerRichContent, assertServiceRichContent, } from '../../../common/utils/content-utils';
export const fromServerFetchComments = (response) => {
    assertDefined(response.additionalData);
    assertDefined(response.response);
    const { followedMemberIds, memberBadges, memberRoles, members, currentMember, currentRating, ratingsSummary, votes, registrationData, contextPermissions, reactions, totalComments, } = response.additionalData;
    return {
        comments: fromServerComments(response),
        members: members?.map(fromServerMember) ?? [],
        currentMember: currentMember ? fromServerMember(currentMember) : undefined,
        memberBadges: memberBadges ? fromServerMemberBadges(memberBadges) : undefined,
        memberRoles: memberRoles ? fromServerMemberRoles(memberRoles) : undefined,
        followedMemberIds: followedMemberIds ?? [],
        registrationData: {
            registrationOpened: registrationData?.registrationOpened ?? false,
            registrationManuallyApproved: registrationData?.registrationManuallyApproved ?? true,
            requireEmailVerification: registrationData?.requireEmailVerification ?? true,
        },
        votes: votes ? fromServerVotes(votes) : [],
        reactions: reactions ? fromServerReactions(reactions) : {},
        ratings: fromServerRatings(ratingsSummary),
        currentRating: fromServerCurrentRating(currentRating),
        permissions: fromServerPermissions(contextPermissions),
        totalComments: totalComments ?? 0,
    };
};
export const fromServerBulkFetchComments = (response) => {
    const normalisedFields = response.reduce((acc, response) => {
        assertDefined(response.additionalData);
        assertDefined(response.response);
        const { followedMemberIds, memberBadges, memberRoles, members, currentMember, registrationData, } = response.additionalData;
        return {
            currentMember: currentMember ? fromServerMember(currentMember) : undefined,
            registrationData: {
                registrationOpened: registrationData?.registrationOpened ?? false,
                registrationManuallyApproved: registrationData?.registrationManuallyApproved ?? true,
                requireEmailVerification: registrationData?.requireEmailVerification ?? true,
            },
            followedMemberIds: uniq([...acc.followedMemberIds, ...(followedMemberIds ?? [])]),
            members: uniqBy([...acc.members, ...(members || [])], 'id').map(fromServerMember),
            memberBadges: {
                ...acc.memberBadges,
                ...(memberBadges ? fromServerMemberBadges(memberBadges) : {}),
            },
            memberRoles: {
                ...acc.memberRoles,
                ...(memberRoles ? fromServerMemberRoles(memberRoles) : {}),
            },
        };
    }, {
        currentMember: undefined,
        members: [],
        memberBadges: {},
        memberRoles: {},
        registrationData: {
            registrationOpened: false,
            registrationManuallyApproved: true,
            requireEmailVerification: true,
        },
        followedMemberIds: [],
    });
    return {
        ...normalisedFields,
        commentsByResourceId: response.map((r) => {
            assertDefined(r.additionalData);
            assertDefined(r.response);
            const { ratingsSummary, votes, contextPermissions, currentRating, reactions, totalComments } = r.additionalData;
            return {
                comments: fromServerComments(r),
                votes: votes ? fromServerVotes(votes) : [],
                ratings: fromServerRatings(ratingsSummary),
                currentRating: fromServerCurrentRating(currentRating),
                permissions: fromServerPermissions(contextPermissions),
                totalComments: totalComments ?? 0,
                reactions: reactions ? fromServerReactions(reactions) : {},
            };
        }),
    };
};
export const fromServerFetchCommentThread = (response) => {
    assertDefined(response.additionalData);
    assertDefined(response.response);
    const { followedMemberIds, registrationData, currentMember, memberBadges, members, votes, currentRating, ratingsSummary, reactions, memberRoles, totalComments, } = response.additionalData;
    return {
        comments: fromServerComments(response),
        permissions: fromServerPermissions(response.additionalData.contextPermissions),
        registrationData: {
            registrationOpened: registrationData?.registrationOpened ?? false,
            registrationManuallyApproved: registrationData?.registrationManuallyApproved ?? true,
            requireEmailVerification: registrationData?.requireEmailVerification ?? true,
        },
        currentMember: currentMember ? fromServerMember(currentMember) : undefined,
        memberBadges: memberBadges ? fromServerMemberBadges(memberBadges) : undefined,
        memberRoles: memberRoles ? fromServerMemberRoles(memberRoles) : undefined,
        members: members?.map(fromServerMember) ?? [],
        followedMemberIds: followedMemberIds ?? [],
        votes: votes ? fromServerVotes(votes) : [],
        reactions: reactions ? fromServerReactions(reactions) : {},
        ratings: fromServerRatings(ratingsSummary),
        currentRating: fromServerCurrentRating(currentRating),
        totalComments: totalComments ?? 0,
    };
};
export const fromServerGetBulkComments = (response) => {
    assertDefined(response.comments);
    assertDefined(response.additionalData);
    const { contextPermissions, votes, currentMember, memberBadges, memberRoles, members, totalComments, } = response.additionalData;
    return {
        comments: response.comments.map(fromServerComment),
        members: members?.map(fromServerMember) ?? [],
        currentMember: currentMember ? fromServerMember(currentMember) : undefined,
        memberBadges: memberBadges ? fromServerMemberBadges(memberBadges) : undefined,
        memberRoles: memberRoles ? fromServerMemberRoles(memberRoles) : undefined,
        votes: votes ? fromServerVotes(votes) : [],
        permissions: fromServerPermissions(contextPermissions),
        totalComments: totalComments ?? 0,
    };
};
const fromServerReactions = (reactions) => {
    return mapValues(reactions, (r) => ({
        total: r.total ?? 0,
        reactions: r.reactions?.map((reaction) => ({
            total: reaction.total ?? 0,
            hasReacted: reaction.hasReacted ?? false,
            identities: reaction.identities?.map(fromServerIdentity).filter(isDefined) ?? [],
            reactionCode: reaction.reactionCode &&
                Object.values(ReactionCodeValue).includes(reaction.reactionCode)
                ? reaction.reactionCode
                : ReactionCodeValue.LIKE,
        })) ?? [],
    }));
};
const fromServerIdentity = (identity) => {
    if (identity?.memberId) {
        return { identityId: identity.memberId, identityType: 'MEMBER' };
    }
    if (identity?.visitorId) {
        return {
            identityId: identity.visitorId,
            identityType: 'ANONYMOUS',
            authorName: identity?.authorName || undefined,
        };
    }
    return undefined;
};
const fromServerRatings = (summary) => {
    return {
        total: summary?.total ?? 0,
        breakdown: [1, 2, 3, 4, 5].reduce((acc, number) => {
            acc[number] = summary?.breakdown?.[number] ?? 0;
            return acc;
        }, {}),
    };
};
export const fromServerCurrentRating = (currentRating) => {
    return {
        rating: currentRating?.rating ?? undefined,
        publishedCommentCount: currentRating?.publishedCommentCount ?? undefined,
    };
};
const fromServerReplies = (replies) => {
    return mapValues(replies, (r) => ({
        replies: r.replies?.map(fromServerComment) ?? [],
        pagingMetadata: fromServerPagingMetadata(r.pagingMetadata),
    }));
};
const fromServerComments = (response) => {
    assertFields(response, ['response', 'additionalData'], 'fromServerComments');
    return {
        comments: response.response.comments.map(fromServerComment),
        pagingMetadata: fromServerPagingMetadata(response.response?.pagingMetadata),
        replies: fromServerReplies(response.response?.commentReplies),
    };
};
export const toServerRatingAction = (ratingAction) => {
    switch (ratingAction.type) {
        case 'ADD':
            return { updateRating: ratingAction.value };
        case 'REMOVE':
            return { removeRating: true };
    }
};
export const fromServerComment = (comment) => {
    assertFields(comment, ['id', 'resourceId'], 'comment');
    assertDefined(comment.id);
    assertDefined(comment.resourceId);
    assertDefined(comment.revision);
    return {
        commentId: comment.id,
        content: fromServerContent(comment.content),
        draftContent: fromServerContent(comment.draftContent),
        resourceId: comment.resourceId,
        createdAt: typeof comment.createdDate === 'string'
            ? comment.createdDate
            : (comment.createdDate ?? new Date(Date.now())).toISOString(),
        updatedAt: typeof comment.updatedDate === 'string'
            ? comment.updatedDate
            : (comment.updatedDate ?? new Date(Date.now())).toISOString(),
        author: fromServerIdentity(comment.author),
        isMarked: comment.marked ?? false,
        isEdited: comment.contentEdited ?? false,
        status: comment.status ?? 'UNKNOWN',
        replyTo: comment.parentComment?.id
            ? {
                commentId: comment.parentComment.id,
                author: fromServerIdentity(comment.parentComment.author),
                status: comment.parentComment.status ?? 'UNKNOWN',
            }
            : undefined,
        voteSummary: {
            upvoteCount: comment.voteSummary?.upvoteCount ?? 0,
            downvoteCount: comment.voteSummary?.upvoteCount ?? 0,
            score: comment.voteSummary?.netVoteCount ?? 0,
        },
        parentCommentId: comment.parentComment?.id ?? undefined,
        rating: comment.rating ?? undefined,
        revisionId: comment.revision,
        repliesCount: comment.replyCount ?? 0,
    };
};
export const toServerComment = (comment) => {
    assertServerRichContent(comment.content?.richContent);
    return {
        ...comment,
        id: comment.commentId,
        revision: comment.revisionId,
        parentComment: comment.parentCommentId ? { id: comment.parentCommentId } : undefined,
        author: { authorName: comment.authorName },
        content: {
            ...comment.content,
            richContent: {
                ...comment.content?.richContent,
                metadata: {
                    ...comment.content?.richContent?.metadata,
                    updatedTimestamp: comment.content?.richContent?.metadata?.updatedTimestamp
                        ? new Date(comment.content?.richContent?.metadata.updatedTimestamp)
                        : undefined,
                    createdTimestamp: comment.content?.richContent?.metadata?.createdTimestamp
                        ? new Date(comment.content?.richContent?.metadata.createdTimestamp)
                        : undefined,
                },
            },
        },
    };
};
export const fromServerPermissions = (permissions) => {
    return {
        viewContext: permissions?.viewContext ?? false,
        createComment: permissions?.createComment ?? false,
        createReply: permissions?.createReply ?? false,
        updateAnyComment: permissions?.updateAnyComment ?? false,
        deleteAnyComment: permissions?.deleteAnyComment ?? false,
        rateResource: permissions?.rateResource ?? false,
        markComment: permissions?.markComment ?? false,
        voteComment: permissions?.voteComment ?? false,
        reportComment: permissions?.reportComment ?? false,
        reactToComment: permissions?.reactToComment ?? false,
    };
};
export const fromServerPagingMetadata = (pagingMetadata) => {
    return {
        count: pagingMetadata?.count ?? 0,
        previousCursor: pagingMetadata?.cursors?.prev ?? undefined,
        nextCursor: pagingMetadata?.cursors?.next ?? undefined,
    };
};
const fromServerContent = (content) => {
    if (content) {
        return {
            richContent: tryResolveServiceRichContent(content?.richContent),
            mentions: content?.mentions?.map(({ memberId, groupId }) => ({
                memberId: memberId ?? undefined,
                groupId: groupId ?? undefined,
            })) ?? [],
        };
    }
};
const fromServerMemberBadges = (memberBadges) => {
    const validEntries = memberBadges.filter((m) => m.badges && m.memberId);
    return mapValues(keyBy(validEntries, 'memberId'), (m) => m.badges?.filter((m) => m.id && m.title) ?? []);
};
const fromServerMemberRoles = (memberRoles) => {
    const validEntries = memberRoles.filter((m) => m.roles && m.memberId);
    return mapValues(keyBy(validEntries, 'memberId'), (m) => m.roles ?? []);
};
const fromServerMember = (member) => {
    assertDefined(member.id);
    assertDefined(member.profile);
    return { ...member, id: member.id, profile: member.profile };
};
const tryResolveServiceRichContent = (content) => {
    try {
        assertServiceRichContent(content);
        return content;
    }
    catch (e) {
        return undefined;
    }
};
export const fromServerFetchCommentHistory = (response) => {
    const { commentContentRevisions, pagingMetadata } = response?.response ?? {};
    const { members } = response?.additionalData ?? {};
    return {
        history: commentContentRevisions
            ?.map((revision) => revision.id
            ? {
                revisionId: revision.id,
                content: tryResolveServiceRichContent(revision.content),
                createdAt: typeof revision.createdDate === 'string'
                    ? revision.createdDate
                    : (revision.createdDate ?? new Date(Date.now())).toISOString(),
                author: resolveRevisionAuthor(revision.author, members),
            }
            : undefined)
            .filter(isDefined) ?? [],
        nextCursor: pagingMetadata?.cursors?.next ?? null,
    };
};
export const fromServerFetchCommentData = (response) => {
    const { members, currentMember, followedMemberIds, votes, currentRating, ratingsSummary, reactions, memberBadges, memberRoles, } = response.additionalData ?? {};
    return {
        members: members?.map(fromServerMember) ?? [],
        currentMember: currentMember ? fromServerMember(currentMember) : undefined,
        followedMemberIds: followedMemberIds ?? [],
        currentRating: fromServerCurrentRating(currentRating),
        ratings: fromServerRatings(ratingsSummary),
        votes: votes ? fromServerVotes(votes) : [],
        reactions: reactions ? fromServerReactions(reactions) : {},
        memberBadges: memberBadges ? fromServerMemberBadges(memberBadges) : undefined,
        memberRoles: memberRoles ? fromServerMemberRoles(memberRoles) : undefined,
    };
};
const resolveRevisionAuthor = (author, members) => {
    if (author?.visitorId) {
        return { type: 'VISITOR', id: author.visitorId };
    }
    if (author?.memberId && members) {
        const memberData = members.find((m) => m.id === author.memberId);
        return memberData
            ? { type: 'MEMBER', member: fromServerMember(memberData) }
            : { type: 'UNKNOWN' };
    }
    return { type: 'UNKNOWN' };
};
//# sourceMappingURL=comments-service-mappers.js.map